import { useCallback, useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import { t } from "@lingui/macro";
import { useSWRConfig } from "swr";
import { FitnessProfiles, FourDPFitnessProfileCalculation } from "@WahooFitness/cloud-client-ts";
import {
  useCloudContext,
  useConfigContext,
  useGuardedUpdater,
  useSnackbarContext,
  useUserContext,
} from "@WahooFitness/wahoo-offline-mfe";
import {
  FitnessProfileAlgorithm,
  FitnessProfileResponseType,
  FourDPFitnessProfileCalculationInputType,
} from "@WahooFitness/cloud-client-types";
import { differenceInYears } from "date-fns";
import { FitnessProfileEditForm } from "@/components/ProfileEdit/useFourDpEdit";

const DEFAULT_PREFERRED_CADENCE = 85;

const useManualCyclingProfileUpdate = ({
  cyclingFitnessProfile,
  handleClose,
}: {
  cyclingFitnessProfile: FitnessProfileResponseType;
  handleClose: () => void;
}) => {
  const { palette } = useTheme();
  const { getCloudClient } = useCloudContext();
  const { wahooToken } = useConfigContext();
  const { update } = useGuardedUpdater();
  const { currentUser } = useUserContext();
  const { enqueueSnackbar } = useSnackbarContext();
  const { mutate } = useSWRConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [showPowerZoneUpdate, setShowPowerZoneUpdate] = useState(false);

  const fitnessProfileClient = useMemo(() => getCloudClient(FitnessProfiles), [getCloudClient]);
  const fourDPFitnessProfileCalculationClient = useMemo(
    () => getCloudClient(FourDPFitnessProfileCalculation),
    [getCloudClient]
  );

  const initialFormItems: FitnessProfileEditForm[] = useMemo(
    () => [
      {
        index: 0,
        subLabel: "FTP",
        time: t`60 min`,
        label: "Threshold Power",
        itemStartColor: palette.zones["4dp"].ftp.main,
        itemEndColor: palette.zones["4dp"].ftp.main,
        value: cyclingFitnessProfile.dim_1 ?? 0,
        endAdornment: t`W`,
        maxValue: 600,
        hasError: false,
        key: "dim_1",
      },
      {
        index: 1,
        subLabel: "MAP",
        time: t`5 min`,
        label: "Maximal Aerobic Power",
        itemStartColor: palette.zones["4dp"].map.main,
        itemEndColor: palette.zones["4dp"].map.main,
        value: cyclingFitnessProfile.dim_2 ?? 0,
        endAdornment: t`W`,
        maxValue: 800,
        hasError: false,
        key: "dim_2",
      },
      {
        index: 2,
        subLabel: "AC",
        time: t`1 min`,
        label: "Anaerobic Capacity",
        itemStartColor: palette.zones["4dp"].ac.main,
        itemEndColor: palette.zones["4dp"].ac.main,
        value: cyclingFitnessProfile.dim_3 ?? 0,
        endAdornment: t`W`,
        maxValue: 1500,
        hasError: false,
        key: "dim_3",
      },
      {
        index: 3,
        subLabel: "NM",
        time: t`5 sec`,
        label: "Neuro-muscular",
        itemStartColor: palette.zones["4dp"].nm.main,
        itemEndColor: palette.zones["4dp"].nm.main,
        value: cyclingFitnessProfile.dim_4 ?? 0,
        endAdornment: t`W`,
        maxValue: 2500,
        hasError: false,
        key: "dim_4",
      },
      {
        index: 4,
        label: t`Threshold heart rate`,
        value: cyclingFitnessProfile.threshold_hr ?? undefined,
        endAdornment: t`BPM`,
        maxValue: 250,
        hasError: false,
        key: "threshold_hr",
      },
      {
        index: 5,
        label: t`Preferred cadence`,
        value: cyclingFitnessProfile.preferred_cadence ?? DEFAULT_PREFERRED_CADENCE,
        endAdornment: t`RPM`,
        maxValue: 200,
        hasError: false,
        key: "preferred_cadence",
        defaultValue: DEFAULT_PREFERRED_CADENCE,
        description: t`Preferred cadence is the cadence you feel the most comfortable when riding at a moderate effort. It is a key component to calculate your Training Load.`,
      },
    ],
    [
      cyclingFitnessProfile.dim_1,
      cyclingFitnessProfile.dim_2,
      cyclingFitnessProfile.dim_3,
      cyclingFitnessProfile.dim_4,
      cyclingFitnessProfile.preferred_cadence,
      cyclingFitnessProfile.threshold_hr,
      palette,
    ]
  );

  const [formData, setFormData] = useState<FitnessProfileEditForm[]>(initialFormItems);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
      const { value } = event.target;
      const sanitizedValue = value === "" ? undefined : +value;
      setFormData((prev) =>
        prev.map((item, i) => (i === index ? { ...item, value: sanitizedValue } : item))
      );
    },
    []
  );

  const handleBlur = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
      const { value } = event.target;
      const sanitizedValue = value === "" ? undefined : +value;
      const hasError =
        !sanitizedValue ||
        sanitizedValue > formData[index].maxValue ||
        sanitizedValue < 0 ||
        sanitizedValue.toString().includes(".");
      setFormData((prev) => prev.map((item, i) => (i === index ? { ...item, hasError } : item)));
    },
    [formData]
  );

  const handleFocus = useCallback((index: number) => {
    setFormData((prev) =>
      prev.map((item, i) => (i === index ? { ...item, hasError: false } : item))
    );
  }, []);

  const handleReset = useCallback(() => {
    setFormData(initialFormItems);
  }, [initialFormItems]);

  const handleCloseModal = useCallback(() => {
    setShowPowerZoneUpdate(false);
    handleReset();
    handleClose();
  }, [handleClose, handleReset]);

  const handleCloseWithMessage = useCallback(
    (hasUpdatedZones?: boolean) => {
      if (hasUpdatedZones === false) {
        enqueueSnackbar({
          message: t`Your Cycling Athlete Profile values were successfully updated.`,
          severity: "success",
        });
      }
      handleCloseModal();
    },
    [enqueueSnackbar, handleCloseModal]
  );

  const formIsTouched = useMemo(
    () =>
      formData.length !== initialFormItems.length
        ? true
        : formData.some((item, index) => item.value !== initialFormItems[index]?.value),
    [formData, initialFormItems]
  );

  const handleSave = async () => {
    setIsLoading(true);
    const isCadenceHrChangeOnly = [0, 1, 2, 3].every(
      (index) =>
        formData[index].value ===
        cyclingFitnessProfile[initialFormItems[index].key as keyof FitnessProfileResponseType]
    );
    const fourDPFitnessProfileCalculationInput: FourDPFitnessProfileCalculationInputType = {
      athlete: {
        FTP: formData[0].value ?? 0,
        MAP: formData[1].value ?? 0,
        AC: formData[2].value ?? 0,
        NM: formData[3].value ?? 0,
        age: currentUser?.birth
          ? differenceInYears(new Date(), new Date(currentUser?.birth + "T00:00:00"))
          : 35,
        weight: Number(currentUser?.weight) || 70,
        sex: currentUser?.gender === 0 ? "male" : "female",
        user_id: currentUser?.id,
      },
      option: "profile_from_athlete_data",
    };
    const newProfileResults = await fourDPFitnessProfileCalculationClient.post(
      wahooToken,
      fourDPFitnessProfileCalculationInput
    );
    const fitnessProfile: FitnessProfileResponseType = {
      ...cyclingFitnessProfile,
      dim_1: formData[0].value ?? 0,
      dim_1_rank: newProfileResults.profile.rank.FTP ?? cyclingFitnessProfile.dim_1_rank,
      dim_2: formData[1].value ?? 0,
      dim_2_rank: newProfileResults.profile.rank.MAP ?? cyclingFitnessProfile.dim_2_rank,
      dim_3: formData[2].value ?? 0,
      dim_3_rank: newProfileResults.profile.rank.AC ?? cyclingFitnessProfile.dim_3_rank,
      dim_4: formData[3].value ?? 0,
      dim_4_rank: newProfileResults.profile.rank.NM ?? cyclingFitnessProfile.dim_4_rank,
      threshold_hr: formData[4].value ?? null,
      preferred_cadence: formData[5].value ?? null,
      fitness_profile_algorithm_id: FitnessProfileAlgorithm.Custom,
    };
    return update(
      () =>
        fitnessProfileClient.updateFitnessProfile(
          cyclingFitnessProfile.id,
          fitnessProfile,
          wahooToken
        ),
      {
        afterUpdate: () => {
          mutate(["userFitnessProfiles", wahooToken]);
          setShowPowerZoneUpdate(!isCadenceHrChangeOnly);
          // setFormIsTouched(false);
          setIsLoading(false);
          isCadenceHrChangeOnly && handleCloseWithMessage(false);
        },
        onUpdateError: () => {
          setIsLoading(false);
          enqueueSnackbar({
            message: t`There was a problem saving your profile. Check your network and try again.`,
            severity: "error",
          });
        },
      }
    );
  };

  const isSaveButtonDisabled = useMemo(
    () => formData.some((item) => item.hasError) || !formIsTouched || isLoading,
    [formData, formIsTouched, isLoading]
  );

  return {
    formData,
    formIsTouched,
    handleInputChange,
    handleBlur,
    handleFocus,
    handleReset,
    handleSave,
    handleCloseModal,
    handleCloseWithMessage,
    isLoading,
    isSaveButtonDisabled,
    showPowerZoneUpdate,
  };
};

export default useManualCyclingProfileUpdate;
